import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
	Conversation,
	fetchMessages,
	getDisplayName,
	getRecipientId,
	markMessagesAsRead,
	Message,
	sendMessage,
} from "@/lib/messaging";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface ConversationViewProps {
	conversation: Conversation | null;
}

export function ConversationView({ conversation }: ConversationViewProps) {
	const { user } = useAuth();
	const [messages, setMessages] = useState<Message[]>([]);
	const [newMessage, setNewMessage] = useState("");
	const [sending, setSending] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	// Load messages for the selected conversation
	useEffect(() => {
		async function loadMessages() {
			if (!conversation || !user) return;

			const messagesData = await fetchMessages(conversation.id);
			setMessages(messagesData);

			// Mark messages as read
			await markMessagesAsRead(conversation.id, user.id);
		}

		loadMessages();

		// Set up real-time subscription for new messages
		if (conversation && user) {
			const channel = supabase
				.channel("public:messages")
				.on(
					"postgres_changes",
					{
						event: "INSERT",
						schema: "public",
						table: "messages",
						filter: `conversation_id=eq.${conversation.id}`,
					},
					(payload) => {
						// Add the new message to the state
						const newMsg = payload.new as Message;
						setMessages((prev) => [...prev, newMsg]);

						// Mark message as read if current user is the recipient
						if (newMsg.recipient_id === user.id) {
							markMessagesAsRead(conversation.id, user.id);
						}
					}
				)
				.subscribe();

			return () => {
				supabase.removeChannel(channel);
			};
		}
	}, [conversation, user]);

	// Scroll to bottom when messages change
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	// Handle sending a new message
	const handleSendMessage = async () => {
		if (!user || !conversation || !newMessage.trim()) return;

		const recipientId = getRecipientId(conversation, user.id);

		setSending(true);
		const success = await sendMessage(
			user.id,
			recipientId,
			newMessage.trim(),
			conversation.id
		);

		if (success) {
			setNewMessage("");
		}

		setSending(false);
	};

	// Handle pressing Enter to send
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	if (!conversation) {
		return (
			<div className="flex items-center justify-center h-full">
				<div className="text-center p-8">
					<MessageSquare className="mx-auto h-12 w-12 text-gray-300 mb-3" />
					<h3 className="text-lg font-medium text-gray-900">
						No conversation selected
					</h3>
					<p className="text-gray-500 mt-1">
						Select a conversation from the list to view messages
					</p>
				</div>
			</div>
		);
	}

	// Get the correct profile image based on whether the user is the retailer or supplier
	const profileImage =
		conversation.retailer_id === user?.id
			? conversation.supplier_profile?.profile_image
			: conversation.retailer_profile?.profile_image;

	return (
		<Card className="flex flex-col h-full">
			<CardHeader className="border-b">
				<CardTitle className="flex items-center gap-3">
					<Avatar>
						<AvatarImage src={profileImage || undefined} />
						<AvatarFallback className="bg-primary/10 text-primary">
							{getDisplayName(conversation, user?.id || "")
								.substring(0, 2)
								.toUpperCase()}
						</AvatarFallback>
					</Avatar>
					<span>{getDisplayName(conversation, user?.id || "")}</span>
				</CardTitle>
			</CardHeader>

			<CardContent className="flex-grow overflow-y-auto p-4 space-y-4 max-h-[50vh]">
				{messages.length === 0 ? (
					<div className="flex items-center justify-center h-full text-gray-500">
						No messages yet. Start the conversation!
					</div>
				) : (
					messages.map((message) => {
						const isSender = message.sender_id === user?.id;

						return (
							<div
								key={message.id}
								className={`flex ${
									isSender ? "justify-end" : "justify-start"
								}`}>
								<div
									className={`max-w-[75%] rounded-lg px-4 py-2 ${
										isSender
											? "bg-accent text-accent-foreground"
											: "bg-gray-100 text-gray-900"
									}`}>
									<p>{message.content}</p>
									<p
										className={`text-xs mt-1 ${
											isSender
												? "text-accent-foreground/80"
												: "text-gray-500"
										}`}>
										{formatDistanceToNow(
											new Date(message.created_at),
											{
												addSuffix: true,
											}
										)}
									</p>
								</div>
							</div>
						);
					})
				)}
				<div ref={messagesEndRef} />
			</CardContent>

			<CardFooter className="border-t p-4">
				<div className="flex w-full gap-2">
					<Textarea
						placeholder="Type your message..."
						value={newMessage}
						onChange={(e) => setNewMessage(e.target.value)}
						onKeyDown={handleKeyDown}
						className="flex-grow min-h-[60px] resize-none"
					/>
					<Button
						onClick={handleSendMessage}
						variant="accent"
						disabled={!newMessage.trim() || sending}
						className="self-end">
						{sending ? (
							<span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
						) : (
							<Send className="h-4 w-4" />
						)}
						<span className="sr-only">Send</span>
					</Button>
				</div>
			</CardFooter>
		</Card>
	);
}
